import { HStack, VStack } from "@chakra-ui/react";
import { Form, Formik, FormikConfig } from "formik";
import { flatten } from "ramda";
import { FC, useCallback, useMemo, useState } from "react";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";

import { GraphQLEnums } from "~/src/__generated__/GraphQLEnums";
import { PopupGroupForm_Query } from "~/src/__relay_artifacts__/PopupGroupForm_Query.graphql";
import { PopupGroupForm_site$key } from "~/src/__relay_artifacts__/PopupGroupForm_site.graphql";
import { FormControl } from "~/src/components/common/forms/FormControl";
import { FormControlGroup } from "~/src/components/common/forms/FormControlGroup";
import { FormControlGroupWithAccordion } from "~/src/components/common/forms/FormControlGroupWithAccordion";
import { FormikDateField } from "~/src/components/common/forms/FormikDateField/FormikDateField";
import { FormikFormButtons } from "~/src/components/common/forms/FormikFormButtons";
import { FormikMultipleSelectField } from "~/src/components/common/forms/FormikMultipleSelectField";
import { FormikRadioButtonsField } from "~/src/components/common/forms/FormikRadioButtonsField";
import { FormikSelectField } from "~/src/components/common/forms/FormikSelectField";
import { FormikSwitchField } from "~/src/components/common/forms/FormikSwitchField";
import { FormikTextareaField } from "~/src/components/common/forms/FormikTextareaField";
import { FormikTextField } from "~/src/components/common/forms/FormikTextField";
import { Switch } from "~/src/components/common/Switch";
import { DeliverDayOfWeeksFieldArray } from "~/src/components/features/deliverDayOfWeek";
import { formatISOToTime } from "~/src/lib/utils/date-utils";

import { ScheduleListField } from "../../global/ScheduleListField";

import { PopupDistributionSettingFields } from "./PopupDistributionSettingFields";
import { FormValues, validationSchema } from "./validationSchema";

export type Props = {
  initialValues?: FormValues;
  siteRef: PopupGroupForm_site$key;
  repeat: boolean;
  onRepeatChange: () => void;
  onCancelClick: () => void;
  onSubmit: FormikConfig<FormValues>["onSubmit"];
  isFunnelDisabled: boolean;
  isHideFunnelForm?: boolean;
  funnelId?: string | null;
};

const query = graphql`
  query PopupGroupForm_Query {
    ...PopupDistributionSettingFields_root
  }
`;

const siteFragment = graphql`
  fragment PopupGroupForm_site on Site {
    funnels {
      edges {
        node {
          id
          name
          confirmPage {
            id
            name
            kind
            url
            sortNumber
          }
          landingPage {
            id
            name
            kind
            url
            sortNumber
          }
          formPage {
            id
            name
            kind
            url
            sortNumber
          }
          thanksPage {
            id
            name
            kind
            url
            sortNumber
          }
        }
      }
    }
    owner {
      schedules(orderBy: { field: START_TIME, direction: ASC }) {
        edges {
          node {
            id
            startTime
            endTime
          }
        }
      }
    }
  }
`;

const defaultInitialValues: FormValues = {
  memo: null,
  name: "",
  funnelId: "",
  pageId: "",
  startDate: null,
  endDate: null,
  devices: GraphQLEnums.Device.map((device) => device.value),
  platforms: GraphQLEnums.Platform.map((platform) => platform.value),
  browsers: GraphQLEnums.Browser.map((browser) => browser.value),
  genders: GraphQLEnums.Gender.map((gender) => gender.value),
  ageRanges: GraphQLEnums.AgeRange.map((ageRange) => ageRange.value),
  annualIncomes: GraphQLEnums.AnnualIncome.map(
    (annualIncome) => annualIncome.value
  ),
  deliverKind: "EQUAL",
  isContentAnalyticsEnabled: false,
  isMulti: "false",
  redisplayPopupTime: 0,
  sourceIds: [],
  excludeSourceIds: [],
  referrers: [],
  excludeReferrers: [],
  visitCount: null,
  visitMatchType: "EQUAL_MATCH_TYPE",
  deliverTargetUrls: [],
  deliverExcludeUrls: [],
  targetCookies: [],
  deliverDayOfWeeks: [],
  displayTargetContents: [],
  disableTargetElements: [],
  scheduleIds: [],
};

const isMultiOptions = [
  { label: "シングル配信", value: "false" },
  { label: "マルチ配信", value: "true" },
];

export const PopupGroupForm: FC<
  React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({
  initialValues = defaultInitialValues,
  siteRef,
  repeat,
  onRepeatChange,
  onCancelClick,
  onSubmit,
  isFunnelDisabled,
  isHideFunnelForm,
  funnelId,
}) => {
  const site = useFragment(siteFragment, siteRef);
  const data = useLazyLoadQuery<PopupGroupForm_Query>(query, {});

  initialValues.funnelId = funnelId ? funnelId : initialValues.funnelId;

  const [funnelValue, setFunnelValue] = useState(initialValues.funnelId);

  const funnelOptions = useMemo(
    () =>
      site?.funnels.edges?.map((edge) => {
        const node = edge?.node;
        if (!node) throw new Error("assertion failed");
        return { label: node.name, value: node.id };
      }) || [],
    [site?.funnels.edges]
  );

  const pageOptions = useMemo(() => {
    const funnel = site?.funnels.edges?.find(
      (edge) => edge?.node?.id === funnelValue
    )?.node;
    if (!funnel) return [];
    const pages = flatten([
      funnel.landingPage,
      funnel.formPage,
      funnel.confirmPage,
      funnel.thanksPage,
    ]).filter((page) => page !== null);
    return pages.map((page) => {
      const pageName =
        page?.kind === "FORM"
          ? page.name + `(${page.sortNumber - 1})`
          : page?.name;
      return {
        label: pageName + `(${page?.url})`,
        value: page?.id,
      };
    });
  }, [site?.funnels.edges, funnelValue]);

  const scheduleOptions = useMemo(
    () =>
      site?.owner.schedules.edges?.flatMap((edge) => {
        if (!edge?.node) {
          return [];
        }
        const { node } = edge;
        const startTime = formatISOToTime(node.startTime);
        const endTime = formatISOToTime(node.endTime);
        const label = `${startTime || ""} ~ ${endTime || ""}`;
        return [{ label, value: node.id }];
      }) || [],
    [site?.owner.schedules.edges]
  );

  const handleSubmit = useCallback<FormikConfig<FormValues>["onSubmit"]>(
    (values, formikHelpers) =>
      onSubmit(
        {
          ...values,
          targetCookies: values.targetCookies.map((cookie) => ({
            ...cookie,
            key: cookie.key.trim(),
            value: cookie.value.trim(),
          })),
        },
        formikHelpers
      ),
    [onSubmit]
  );

  return (
    <Formik<FormValues>
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount={false}
      validateOnChange={false}
      onSubmit={handleSubmit}
    >
      {({ values }) => (
        <Form>
          <VStack spacing="24px" alignItems="flex-start">
            <FormControlGroup>
              <FormControl required label="ポップアップグループ名">
                <FormikTextField
                  type="text"
                  mode="fill"
                  name="name"
                  rightAdornment={`${values.name.length}/50`}
                />
              </FormControl>
              {!isHideFunnelForm && (
                <>
                  <FormControl required label="実施するファネル">
                    <FormikSelectField
                      isDisabled={isFunnelDisabled}
                      name="funnelId"
                      onSetStateAction={setFunnelValue}
                      options={funnelOptions}
                    />
                  </FormControl>
                  <FormControl required label="実施するページ">
                    <FormikSelectField name="pageId" options={pageOptions} />
                  </FormControl>
                </>
              )}
              <FormControl label="メモ">
                <FormikTextareaField mode="fill" name="memo" />
              </FormControl>
              <FormControl label="コンテンツレポート">
                <FormikSwitchField name="isContentAnalyticsEnabled" />
              </FormControl>
            </FormControlGroup>

            <FormControlGroup>
              <FormControl label="期間設定">
                <Switch isChecked={repeat} onChange={onRepeatChange} />
              </FormControl>
              {repeat && (
                <>
                  <FormControl label="実施期間">
                    <HStack spacing={"36px"}>
                      <FormikDateField
                        name="startDate"
                        placeholder="開始日を選択"
                      />
                      <FormikDateField
                        name="endDate"
                        placeholder="終了日を選択"
                      />
                    </HStack>
                  </FormControl>
                  <ScheduleListField
                    name="scheduleIds"
                    label="実施時刻"
                    options={scheduleOptions}
                  />
                  <FormControl label="実施曜日">
                    <DeliverDayOfWeeksFieldArray name="deliverDayOfWeeks" />
                  </FormControl>
                </>
              )}
              <FormControl required label="再表示禁止期間">
                <FormikTextField
                  type="number"
                  mode="fill"
                  name="redisplayPopupTime"
                  rightAdornment="分"
                />
              </FormControl>
            </FormControlGroup>

            <FormControlGroupWithAccordion description="配信セグメント">
              <FormControl label="デバイス">
                <FormikMultipleSelectField
                  name="devices"
                  options={GraphQLEnums.Device}
                />
              </FormControl>
              <FormControl label="プラットフォーム">
                <FormikMultipleSelectField
                  name="platforms"
                  options={GraphQLEnums.Platform}
                />
              </FormControl>
              <FormControl label="ブラウザ">
                <FormikMultipleSelectField
                  name="browsers"
                  options={GraphQLEnums.Browser}
                />
              </FormControl>
              <FormControl label="性別">
                <FormikMultipleSelectField
                  name="genders"
                  options={GraphQLEnums.Gender}
                />
              </FormControl>
              <FormControl label="年齢">
                <FormikMultipleSelectField
                  name="ageRanges"
                  options={GraphQLEnums.AgeRange}
                />
              </FormControl>
              <FormControl label="年収">
                <FormikMultipleSelectField
                  name="annualIncomes"
                  options={GraphQLEnums.AnnualIncome}
                />
              </FormControl>
            </FormControlGroupWithAccordion>

            <FormControlGroupWithAccordion description="配信設定">
              <PopupDistributionSettingFields rootRef={data} />
            </FormControlGroupWithAccordion>

            <FormControlGroup>
              <FormControl required label="配信タイプ">
                <FormikRadioButtonsField
                  name="isMulti"
                  options={isMultiOptions}
                />
              </FormControl>
              <FormControl required label="配信方法">
                <FormikRadioButtonsField
                  name="deliverKind"
                  options={GraphQLEnums.DeliverKind}
                />
              </FormControl>
            </FormControlGroup>

            <FormikFormButtons onCancelClick={onCancelClick} />
          </VStack>
        </Form>
      )}
    </Formik>
  );
};
